import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Shield, CheckCircle, AlertTriangle, Package, Calendar, User, Hash, Loader2, XCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  Active: "bg-green-500/20 text-green-400 border-green-500/30",
  Stolen: "bg-red-500/20 text-red-400 border-red-500/30",
  Sold: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  Discarded: "bg-gray-500/20 text-gray-400 border-gray-500/30"
};

export default function VerifyTool() {
  const { toolId } = useParams();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTool();
  }, [toolId]);

  const fetchTool = async () => {
    try {
      const response = await fetch(`${API}/public/verify/${toolId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError("Tool not found in TOOLIO registry");
        } else {
          setError("Unable to verify tool");
        }
        return;
      }

      const data = await response.json();
      setTool(data);
    } catch (err) {
      setError("Unable to connect to verification service");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unknown";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
    } catch {
      return "Unknown";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying registration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
          <div className="flex items-center justify-center px-4 py-3">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              <span className="font-heading text-xl font-bold">TOOLIO</span>
            </Link>
          </div>
        </header>
        <main className="p-4 lg:p-8 max-w-2xl mx-auto">
          <Card className="border-red-500/30 bg-red-500/5">
            <CardContent className="p-8 text-center">
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold mb-2">Verification Failed</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Link to="/check">
                <Button variant="outline">Search for a Tool</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-center px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-bold">TOOLIO</span>
          </Link>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-2xl mx-auto">
        {/* Verified Badge */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full px-6 py-3">
            <CheckCircle className="w-6 h-6" />
            <span className="font-heading font-bold uppercase">Verified Registration</span>
          </div>
        </div>

        {/* Tool Image */}
        {tool.image_url && (
          <Card className="border-border bg-card mb-6 overflow-hidden">
            <img 
              src={tool.image_url} 
              alt={`${tool.manufacturer} ${tool.tool_type}`}
              className="w-full h-64 object-cover"
            />
          </Card>
        )}

        {/* Tool Details */}
        <Card className="border-border bg-card mb-6">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="font-heading text-xl uppercase">
                {tool.manufacturer} {tool.tool_type}
              </CardTitle>
              <Badge className={`${STATUS_STYLES[tool.status]} border font-mono text-sm uppercase`}>
                {tool.status === "Stolen" && <AlertTriangle className="w-4 h-4 mr-1" />}
                {tool.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase">TOOLIO ID</p>
                <p className="font-mono text-sm">{tool.tool_id}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase">Serial Number</p>
                <p className="font-mono text-sm">{tool.serial_number}</p>
              </div>
              {tool.model_number && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase">Model Number</p>
                  <p className="font-mono text-sm">{tool.model_number}</p>
                </div>
              )}
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase">Tool Type</p>
                <p className="text-sm">{tool.tool_type}</p>
              </div>
              {tool.estimated_value && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase">Replacement Value</p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-sm">£{Number(tool.estimated_value).toFixed(2)}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      tool.price_source === 'Retail' 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }`}>
                      {tool.price_source === 'Retail' ? 'Retail' : 'Estimated'}
                    </span>
                  </div>
                  {tool.scan_price && tool.scan_price !== tool.estimated_value && (
                    <p className="text-xs text-muted-foreground mt-1">
                      TOOLIO scan: <span className="font-mono">£{Number(tool.scan_price).toFixed(2)}</span>
                      <span className={`ml-1 ${tool.scan_price_source === 'Retail' ? 'text-green-400' : 'text-yellow-400'}`}>
                        ({tool.scan_price_source || 'Estimated'})
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>

            <hr className="border-border" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Registered</p>
                  <p className="text-sm font-medium">{formatDate(tool.registered_date)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Registered Owner</p>
                  <p className="text-sm font-medium">{tool.owner_name}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stolen Warning */}
        {tool.status === "Stolen" && (
          <Card className="border-red-500/50 bg-red-500/10 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-heading font-bold text-red-400 uppercase">Reported Stolen</h3>
                  <p className="text-sm text-red-300/80 mt-1">
                    This tool has been reported as stolen. If you have information about this tool's whereabouts, 
                    please contact the police with the Crime Reference Number provided by the owner.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Footer */}
        <Card className="border-border bg-card/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground text-center">
              This page confirms that the tool above was registered with TOOLIO on the date shown. 
              TOOLIO is a power tool registration service that helps prove ownership and aids recovery of stolen tools.
              {tool.price_source === 'Retail' && " Retail values are live UK retail prices sourced from online retailers at the time of registration."}
            </p>
            <div className="flex justify-center mt-4">
              <Link to="/">
                <Button variant="outline" size="sm">Visit TOOLIO</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
